import { Card, CardContent, Typography, Chip, Skeleton, Tooltip, Box } from '@mui/material';
import { InfoOutlined } from '@mui/icons-material';
import type { MetricCardProperties } from '@/types/application/common/components/cards/metric-card';

const statusColors = {
  success: { main: '#10b981', bg: '#d1fae5' },
  warning: { main: '#f59e0b', bg: '#fef3c7' },
  error: { main: '#ef4444', bg: '#fee2e2' },
};

export function MetricCard({ title, value, subtitle, status = 'success', chipLabel, onClick, description }: MetricCardProperties) {
  const colors = statusColors[status];

  return (
    <Card 
      sx={{ 
        height: '100%',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'all 0.2s ease-in-out',
        '&:hover': onClick ? {
          transform: 'translateY(-2px)',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
        } : {},
      }}
      onClick={onClick}
    >
      <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography variant="caption" color="text.secondary" fontWeight={500}>
            {title}
          </Typography>
          {description && (
            <Tooltip 
              title={description} 
              arrow 
              placement="top"
              componentsProps={{
                tooltip: {
                  sx: {
                    maxWidth: 400,
                    fontSize: '0.75rem',
                    lineHeight: 1.5,
                    bgcolor: 'rgba(0, 0, 0, 0.9)',
                  },
                },
              }}
            >
              <InfoOutlined 
                sx={{ 
                  fontSize: 14, 
                  color: 'text.secondary',
                  cursor: 'help',
                  '&:hover': { color: 'primary.main' },
                }} 
              />
            </Tooltip>
          )}
        </Box>
        <Typography variant="h4" sx={{ mt: 0.5, color: colors.main, fontWeight: 700 }}>
          {value}
        </Typography>
        {subtitle && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            {subtitle}
          </Typography>
        )}
        {chipLabel && (
          <Chip
            size="small"
            label={chipLabel}
            sx={{ mt: 1, bgcolor: colors.bg, color: colors.main, fontWeight: 500 }}
          />
        )}
      </CardContent>
    </Card>
  );
}

export function MetricCardSkeleton() {
  return (
    <Card>
      <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
        <Skeleton width={100} height={14} />
        <Skeleton width={60} height={36} sx={{ mt: 0.5 }} />
        <Skeleton width={140} height={14} sx={{ mt: 0.5 }} />
        <Skeleton width={80} height={24} sx={{ mt: 1 }} />
      </CardContent>
    </Card>
  );
}

export default MetricCard;

